/**
 * Behavioral tracker — counts answer revisions, focus losses, idle pauses,
 * and captures the initial answer per step.
 *
 * Resets on step change (keyed on currentIndex). Provides a snapshot getter
 * for the submit ceremony to read at submission time.
 */
import { useRef, useCallback, useEffect } from 'react';
import type { StepAnswer } from '../types';
import { useIdleDetection } from './useIdleDetection';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BehavioralMetrics {
  revisionCount: number;
  initialResponseJson: unknown | null;
  focusLossCount: number;
  idleCount: number;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useBehavioralTracker(currentIndex: number) {
  const metricsRef = useRef<BehavioralMetrics>({
    revisionCount: 0,
    initialResponseJson: null,
    focusLossCount: 0,
    idleCount: 0,
  });

  const hasInitialAnswerRef = useRef(false);
  const lastAnswerJsonRef = useRef<string>('');

  // Reset on step change
  useEffect(() => {
    metricsRef.current = {
      revisionCount: 0,
      initialResponseJson: null,
      focusLossCount: 0,
      idleCount: 0,
    };
    hasInitialAnswerRef.current = false;
    lastAnswerJsonRef.current = '';
  }, [currentIndex]);

  /**
   * Call on every UPDATE_ANSWER dispatch to track revisions.
   * Captures the initial answer on first call, counts meaningful changes thereafter.
   */
  const recordAnswerChange = useCallback((answer: StepAnswer) => {
    const serialized = JSON.stringify(answer);

    if (!hasInitialAnswerRef.current) {
      // First answer — capture as initial
      hasInitialAnswerRef.current = true;
      metricsRef.current.initialResponseJson = answer;
      lastAnswerJsonRef.current = serialized;
      return;
    }

    // Subsequent change — count if meaningfully different
    if (serialized !== lastAnswerJsonRef.current) {
      metricsRef.current.revisionCount++;
      lastAnswerJsonRef.current = serialized;
    }
  }, []);

  // ── Focus/blur tracking ─────────────────────────────────────────────────

  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        metricsRef.current.focusLossCount++;
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // ── Idle tracking ───────────────────────────────────────────────────────

  useIdleDetection(
    {
      onIdle: () => {
        metricsRef.current.idleCount++;
      },
      onActive: () => {},
    },
    [currentIndex]
  );

  /**
   * Snapshot current metrics for submission. Call at submit time.
   */
  const getMetrics = useCallback((): BehavioralMetrics => {
    return { ...metricsRef.current };
  }, []);

  return { recordAnswerChange, getMetrics };
}
